import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { getEmojiByType } from '../constants';
import useEmojiReactionsSubscription from '../hooks/useEmojiReactionsSubscription';
// import { useSubscription } from '../contexts/Appsync/Appsync';
// import { SUBSCRIBE_EMOJI_REACTIONS } from '../graphql/operations';

const EmojiReactions = forwardRef(
  ({ containerRef = { current: null }, containerDimensions = {} }, ref) => {
    const [reactions, setReactions] = useState([]);
    const idCounterRef = useRef(0);
    // Use the custom hook for emoji subscription
    const { subscribe, subscriptionError } = useEmojiReactionsSubscription();

    const createReaction = useCallback(
      (emoji, containerWidth, containerHeight) => {
        idCounterRef.current += 1;

        // ─── Completely random X within [0, containerWidth) ───
        // Subtract a bit of margin so the emoji doesn't “bleed” offscreen.
        const MARGIN = 20;
        const randomX = MARGIN + Math.random() * (containerWidth - MARGIN * 2);

        // ─── Random Y near the bottom (you can tweak as needed) ───
        // For example, start anywhere between 80% and 100% of the container height:
        const bottomRange = 0.2 * containerHeight;
        const randomY = containerHeight - Math.random() * bottomRange;

        return {
          emoji,
          id: `${Date.now()}-${idCounterRef.current}`,
          x: randomX,
          y: randomY,
          delay: 0,
          isLocal: false
        };
      },
      []
    );
    // Set up the subscription using the hook
    // The hook handles online/offline status and subscription lifecycle
    useEffect(() => {
      const cleanup = subscribe((emojiEvent) => {
        const containerWidth =
          containerDimensions?.width ||
          containerRef.current?.clientWidth ||
          360;
        const containerHeight =
          containerDimensions?.height ||
          containerRef.current?.clientHeight ||
          640;

        const newReaction = createReaction(
          emojiEvent.emoji,
          containerWidth,
          containerHeight
        );

        setReactions((prev) => [...prev, newReaction]);
      });

      return cleanup;
    }, [subscribe, containerDimensions, containerRef, createReaction]);

    // Handle subscription errors
    useEffect(() => {
      if (subscriptionError) {
        console.warn(
          'Emoji subscription encountered an error:',
          subscriptionError
        );
        // Optionally, you could implement retry logic here
      }
    }, [subscriptionError]);

    // Cleanup effect
    useEffect(() => {
      const cleanupInterval = setInterval(() => {
        setReactions((prev) =>
          prev.filter((reaction) => {
            const animationDuration = 3000; // Match the motion.div animation duration
            const maxDelay = 800;
            const totalDuration = animationDuration + maxDelay + 500;
            const timestamp = parseInt(reaction.id.split('-')[0], 10);
            const age = Date.now() - timestamp;
            return age < totalDuration;
          })
        );
      }, 2000);

      return () => {
        if (cleanupInterval) clearInterval(cleanupInterval);
      };
    }, []);

    // Expose handleLocalEmoji through ref
    useImperativeHandle(ref, () => ({
      handleLocalEmoji: (emoji) => {
        const containerWidth =
          containerDimensions?.width ||
          containerRef.current?.clientWidth ||
          360;
        const containerHeight =
          containerDimensions?.height ||
          containerRef.current?.clientHeight ||
          640;

        // Generate random x position across the full width
        const randomX = Math.random() * (containerWidth - 50); // Subtract some padding

        const newReaction = {
          emoji: emoji.emojiType,
          id: `${Date.now()}-local-${idCounterRef.current++}`,
          x: randomX,
          y: containerHeight - 20, // Start slightly above bottom
          delay: 0,
          isLocal: true
        };

        setReactions((prev) => [...prev, newReaction]);
      }
    }));

    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-40">
        <AnimatePresence>
          {reactions.map((reaction) => (
            <motion.div
              key={reaction.id}
              initial={{ x: reaction.x, y: reaction.y, opacity: 0, scale: 0.5 }}
              animate={{
                x: reaction.x + (Math.random() * 40 - 20),
                y: Math.max(
                  0,
                  reaction.y - (containerRef.current?.clientHeight || 640) * 0.6
                ),
                opacity: [0, 1, 1, 0],
                scale: [0.5, 1.2, 1, 0.8]
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{
                duration: 3, // Increased from 2 to 3 seconds
                delay: reaction.delay,
                ease: 'easeOut'
              }}
              className="absolute text-2xl"
            >
              {getEmojiByType(reaction.emoji)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );
  }
);

EmojiReactions.defaultProps = {
  containerRef: { current: null },
  containerDimensions: {}
};

EmojiReactions.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.any }),
  containerDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  })
};

EmojiReactions.displayName = 'EmojiReactions';

export default EmojiReactions;
